import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { BarChart3, Users, Briefcase, TrendingUp, Award, Activity, ArrowRight, Loader2 } from 'lucide-react';
import api from '../../lib/api';
import Button from '../../components/ui/Button';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import TrendIndicator from '../../components/ui/TrendIndicator';
import MiniSparkline from '../../components/ui/MiniSparkline';
import { useAuthStore } from '../../stores/authStore';
import { requiresOrganizationOnboarding } from '../../lib/onboarding';
import { formatPercentOneDecimal } from '../../lib/utils';
import { StatNumber, StatsGridSkeleton } from '../../components/ui/StatsLoading';
import { AIInsightsPanel } from '../../components/AIInsightsPanel';

function inviteStatusPill(status: string) {
  const s = status.toUpperCase();
  if (s === 'ACCEPTED') return 'status-pill status-pill-pos';
  if (['PENDING', 'MODIFICATION_REQUESTED'].includes(s)) return 'status-pill status-pill-warn';
  if (['REJECTED', 'WITHDRAWN'].includes(s)) return 'status-pill status-pill-danger';
  return 'status-pill status-pill-muted';
}

interface AnalyticsData {
  kols: {
    total: number;
    active: number;
    totalReach: number;
    avgEngagement: string;
  };
  jobs: {
    total: number;
    byStatus: Record<string, number>;
  };
  invitations: {
    total: number;
    byStatus: Record<string, number>;
  };
  topKols: Array<{
    id: string;
    name: string;
    invitations: number;
    accepted: number;
    acceptanceRate: number;
  }>;
  recentActivity: Array<{
    id: string;
    kolName: string;
    jobTitle: string;
    status: string;
    createdAt: string;
  }>;
}

export default function AgencyAnalytics() {
  const { user } = useAuthStore();
  const [exportingPdf, setExportingPdf] = useState(false);

  const onboardingBlocks = !!(user && requiresOrganizationOnboarding(user));

  // Mock trend data (in production, fetch from API)
  const kolsTrend = [18, 19, 20, 21, 22, 23, 24];
  const jobsTrend = [12, 11, 10, 10, 9, 9, 9];

  const analyticsQuery = useQuery({
    queryKey: ['agency', 'analytics'],
    queryFn: async () => (await api.get('/analytics/agency')).data as AnalyticsData,
    staleTime: 5 * 60 * 1000,
    enabled: !!user && !onboardingBlocks,
  });

  const refetching = analyticsQuery.isFetching && !analyticsQuery.isPending;

  const exportAnalyticsPdf = async () => {
    try {
      setExportingPdf(true);
      const res = await api.get('/analytics/agency/export.pdf', { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `agency-analytics-${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      alert('Could not export PDF. Ensure you are logged in as an agency user.');
    } finally {
      setExportingPdf(false);
    }
  };

  if (!user) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-glass-text-3">Loading analytics...</div>
        </div>
      </div>
    );
  }

  if (onboardingBlocks) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-glass-text-3">Complete organization setup to unlock analytics.</div>
        </div>
      </div>
    );
  }

  if (analyticsQuery.isPending) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title flex items-center gap-2">
              <BarChart3 className="w-8 h-8 text-accent2" />
              Analytics Dashboard
            </h1>
            <p className="text-sm text-glass-text-2 mt-1">Real-time performance insights and trends</p>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <StatsGridSkeleton count={2} className="lg:col-span-2 lg:grid-cols-2" />
        </div>
        <StatsGridSkeleton count={4} className="grid-cols-2 md:grid-cols-4" />
      </div>
    );
  }

  if (analyticsQuery.isError) {
    const err = analyticsQuery.error;
    const msg =
      isAxiosError(err) && err.response?.status === 403
        ? 'You need an agency organization membership before analytics is available.'
        : isAxiosError(err)
          ? String(err.response?.data?.error || err.message || 'Failed to load analytics')
          : 'Failed to load analytics';
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-glass-text-3">{msg}</div>
        </div>
      </div>
    );
  }

  const data = analyticsQuery.data;
  if (!data) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-glass-text-3">No analytics data available</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <BarChart3 className="w-8 h-8 text-accent2" />
            Analytics Dashboard
          </h1>
          <p className="text-sm text-glass-text-2 mt-1">Real-time performance insights and trends</p>
        </div>
        <Button
          type="button"
          variant="outline"
          className="gap-2"
          disabled={exportingPdf}
          onClick={() => void exportAnalyticsPdf()}
        >
          {exportingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <Activity className="w-4 h-4" />}
          {exportingPdf ? 'Exporting…' : 'Export PDF report'}
        </Button>
      </div>

      <AIInsightsPanel mode="agency" title="AI Portfolio Insights" />

      {/* Grouped KPI Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        
        {/* Card 1: KOLs & Jobs */}
        <Card className="p-5 hover:-translate-y-0.5 transition-all duration-150">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:divide-x sm:divide-glass">
              
              {/* Total KOLs */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-accent2/15 border border-accent2/30 rounded-lg">
                      <Users className="w-5 h-5 text-accent2" />
                    </div>
                    <p className="text-sm font-semibold text-glass-text">Total KOLs</p>
                  </div>
                  <TrendIndicator value={15.2} />
                </div>
                
                <div className="flex items-baseline gap-2">
                  <StatNumber loading={refetching} className="stat-display text-[1.85rem] text-glass-text">
                    {data.kols.total}
                  </StatNumber>
                  <p className="text-lg text-glass-text-3 font-medium">KOLs</p>
                </div>
                
                <p className="text-sm text-emerald-500 dark:text-emerald-300 font-semibold mt-2">
                  ↑ +3 new this month
                </p>
                
                <div className="mt-4">
                  <MiniSparkline data={kolsTrend} color="rgb(var(--accent-rgb))" height={40} />
                  <p className="text-xs text-glass-text-3 mt-2">Last 7 days trend</p>
                </div>
                
                <div className="mt-4 pt-4 border-t border-glass flex items-center justify-between">
                  <span className="text-xs text-glass-text-2">{data.kols.active} active</span>
                  <span className="text-xs text-accent2 font-medium cursor-pointer hover:underline inline-flex items-center gap-1">
                    View all <ArrowRight className="w-3 h-3" />
                  </span>
                </div>
              </div>
              
              {/* Total Jobs */}
              <div className="sm:pl-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-emerald-500/15 border border-emerald-500/30 rounded-lg">
                      <Briefcase className="w-5 h-5 text-emerald-500 dark:text-emerald-300" />
                    </div>
                    <p className="text-sm font-semibold text-glass-text">Total Jobs</p>
                  </div>
                  <TrendIndicator value={-8.3} />
                </div>
                
                <div className="flex items-baseline gap-2">
                  <StatNumber loading={refetching} className="stat-display text-[1.85rem] text-glass-text">
                    {data.jobs.total}
                  </StatNumber>
                  <p className="text-lg text-glass-text-3 font-medium">Jobs</p>
                </div>
                
                <p className="text-sm text-glass-text-2 font-semibold mt-2">
                  {data.jobs.byStatus['ACTIVE'] || 0} active now
                </p>
                
                <div className="mt-4">
                  <MiniSparkline data={jobsTrend} color="#10B981" height={40} />
                  <p className="text-xs text-glass-text-3 mt-2">Last 7 days trend</p>
                </div>
                
                <div className="mt-4 pt-4 border-t border-glass flex items-center justify-between">
                  <span className="text-xs text-glass-text-2">{data.jobs.byStatus['DRAFT'] || 0} drafts</span>
                  <span className="text-xs text-emerald-500 dark:text-emerald-300 font-medium cursor-pointer hover:underline inline-flex items-center gap-1">
                    Manage <ArrowRight className="w-3 h-3" />
                  </span>
                </div>
              </div>
              
            </div>
          </CardContent>
        </Card>

        {/* Card 2: Reach & Engagement — real metrics deferred to phase 2 */}
        <Card className="p-5 hover:-translate-y-0.5 transition-all duration-150">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:divide-x sm:divide-glass">
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="p-2 bg-purple-500/15 border border-purple-500/30 rounded-lg">
                    <TrendingUp className="w-5 h-5 text-purple-500 dark:text-purple-300" />
                  </div>
                  <p className="text-sm font-semibold text-glass-text">Total Reach</p>
                </div>
                <p className="text-2xl font-semibold tracking-tightest text-glass-text-3">Coming Soon!</p>
                <p className="text-sm text-glass-text-3 mt-2">
                  Aggregated reach across platforms will ship in a later release.
                </p>
              </div>
              <div className="sm:pl-6">
                <div className="flex items-center gap-2 mb-3">
                  <div className="p-2 bg-orange-500/15 border border-orange-500/30 rounded-lg">
                    <Activity className="w-5 h-5 text-orange-500 dark:text-orange-300" />
                  </div>
                  <p className="text-sm font-semibold text-glass-text">Avg Engagement</p>
                </div>
                <p className="text-2xl font-semibold tracking-tightest text-glass-text-3">Coming Soon!</p>
                <p className="text-sm text-glass-text-3 mt-2">
                  Engagement benchmarks will be available once social analytics are connected.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
      </div>

      {/* Invitation Stats */}
      <Card className="p-5">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-xl font-semibold tracking-tightest text-glass-text">Invitation Performance</CardTitle>
            <TrendIndicator value={8.2} label="vs last month" />
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="rounded-xl border border-glass bg-glass-2 p-4">
              <p className="text-xs font-medium text-glass-text-3 uppercase tracking-[0.05em] mb-1">Total Sent</p>
              <StatNumber loading={refetching} className="stat-display text-[1.85rem] text-glass-text">
                {data.invitations.total}
              </StatNumber>
            </div>
            <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/10 p-4">
              <p className="text-xs font-medium text-glass-text-3 uppercase tracking-[0.05em] mb-1">Accepted</p>
              <StatNumber loading={refetching} className="stat-display text-[1.85rem] text-emerald-500 dark:text-emerald-300">
                {data.invitations.byStatus['ACCEPTED'] || 0}
              </StatNumber>
            </div>
            <div className="rounded-xl border border-amber-400/20 bg-amber-400/10 p-4">
              <p className="text-xs font-medium text-glass-text-3 uppercase tracking-[0.05em] mb-1">Pending</p>
              <StatNumber loading={refetching} className="stat-display text-[1.85rem] text-amber-500 dark:text-amber-300">
                {data.invitations.byStatus['PENDING'] || 0}
              </StatNumber>
            </div>
            <div className="rounded-xl border border-rose-500/20 bg-rose-500/10 p-4">
              <p className="text-xs font-medium text-glass-text-3 uppercase tracking-[0.05em] mb-1">Declined</p>
              <StatNumber loading={refetching} className="stat-display text-[1.85rem] text-rose-500 dark:text-rose-300">
                {data.invitations.byStatus['REJECTED'] || 0}
              </StatNumber>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Top Performing KOLs */}
      <Card className="p-5">
        <CardHeader>
          <CardTitle className="text-xl font-semibold tracking-tightest text-glass-text flex items-center gap-2">
            <Award className="w-5 h-5 text-amber-400 dark:text-amber-300" />
            Top Performing KOLs
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data.topKols && data.topKols.length > 0 ? (
            <div className="space-y-3">
              {data.topKols.map((kol, index) => (
                <div key={kol.id} className="flex items-center justify-between p-4 rounded-xl border border-glass bg-glass-2 hover:-translate-y-0.5 hover:bg-glass-3 transition-all duration-150">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white ${
                      index === 0 ? 'bg-amber-400' : index === 1 ? 'bg-glass-text-3' : 'bg-orange-500'
                    }`}>
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-semibold text-glass-text">{kol.name}</p>
                      <p className="text-sm text-glass-text-3">
                        {kol.invitations} invitations • {kol.accepted} accepted
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="stat-display text-[1.85rem] text-emerald-500 dark:text-emerald-300">{formatPercentOneDecimal(kol.acceptanceRate)}</p>
                    <p className="text-xs text-glass-text-3">Acceptance Rate</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-glass-text-3 py-8">No KOL performance data yet</p>
          )}
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <Card className="p-5">
        <CardHeader>
          <CardTitle className="text-xl font-semibold tracking-tightest text-glass-text">Recent Activity</CardTitle>
        </CardHeader>
        <CardContent>
          {data.recentActivity && data.recentActivity.length > 0 ? (
            <div className="space-y-3">
              {data.recentActivity.map((activity) => (
                <div key={activity.id} className="flex items-center justify-between p-3 rounded-xl border-l-4 border-l-accent2 border-glass bg-accent2/5 hover:-translate-y-0.5 transition-all duration-150">
                  <div>
                    <p className="font-semibold text-glass-text">{activity.kolName}</p>
                    <p className="text-sm text-glass-text-3">{activity.jobTitle}</p>
                  </div>
                  <div className="text-right">
                    <span className={inviteStatusPill(activity.status)}>{activity.status}</span>
                    <p className="text-xs text-glass-text-3 mt-1">
                      {new Date(activity.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-glass-text-3 py-8">No recent activity</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
